import { queryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";

import {
  getCurrentTeamLeader,
  getTeamBreakTotals,
  getTeamDay,
  getTeamAttendance,
} from "./tl-queries";

/** An ended, expired or blocked session will not fix itself: don't retry it. */
const retryUnlessClientError = (failureCount: number, error: unknown) =>
  !(error instanceof AxiosError && error.response && error.response.status < 500) && failureCount < 3;

export const getCurrentTeamLeaderQueryOptions = (key: string) =>
  queryOptions({
    // The token is part of the key, so a session that ended cannot leave a
    // cached error behind for the next login to inherit.
    queryKey: ["teamleader", "current", key],
    queryFn: () => getCurrentTeamLeader(key),
    // Re-checked on focus and every minute, so a session ended on the server
    // also ends in a tab that is already open.
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    retry: retryUnlessClientError,
    enabled: !!key,
  });

// The day view is the monitoring surface: it must be current on every refresh
// and whenever the tab regains focus.
export const getTeamDayQueryOptions = (key: string, date: Date) =>
  queryOptions({
    queryKey: ["teamleader", "day", date.toISOString().slice(0, 10)],
    queryFn: () => getTeamDay(key)(date),
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    enabled: !!key,
  });

export const getTeamBreakTotalsQueryOptions = (key: string, startDate: Date, endDate: Date) =>
  queryOptions({
    queryKey: ["teamleader", "totals", startDate.toISOString().slice(0, 10), endDate.toISOString().slice(0, 10)],
    queryFn: () => getTeamBreakTotals(key)({ startDate, endDate }),
    refetchOnWindowFocus: false,
    enabled: !!key,
  });

export const getTeamAttendanceQueryOptions = (key: string, date: Date) =>
  queryOptions({
    queryKey: ["teamleader", "attendance", date.toISOString().slice(0, 10)],
    queryFn: () => getTeamAttendance(key)(date),
    refetchOnWindowFocus: true,
    enabled: !!key,
  });
